import { motion } from "framer-motion";
import { useMemo } from "react";
import {
  Salad, Award, Heart, Clock, Star, Users, CheckCircle,
  ArrowRight, Leaf, Utensils, Activity, Droplet, Sun,
  Scale, ChevronDown, Zap, Shield, HelpCircle,
} from "lucide-react";

const FADE_UP = {
  hidden: { y: 30, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

/* ── Nutrients data ─────────────────────────────────────────────────────── */
const nutrients = [
  {
    name: "Protein",
    icon: <Droplet size={28} />,
    description: "Build and repair tissues",
    gradient: "from-blue-500 to-cyan-500",
    bg: "from-blue-950/80 to-blue-900/60",
    glow: "rgba(59,130,246,0.25)",
  },
  {
    name: "Carbs",
    icon: <Leaf size={28} />,
    description: "Primary energy source",
    gradient: "from-lime-500 to-green-500",
    bg: "from-lime-950/80 to-green-900/60",
    glow: "rgba(132,204,22,0.25)",
  },
  {
    name: "Healthy Fats",
    icon: <Utensils size={28} />,
    description: "Essential for hormones",
    gradient: "from-amber-500 to-yellow-400",
    bg: "from-amber-950/80 to-yellow-900/60",
    glow: "rgba(245,158,11,0.25)",
  },
  {
    name: "Fiber",
    icon: <Scale size={28} />,
    description: "Supports digestion",
    gradient: "from-violet-500 to-purple-500",
    bg: "from-violet-950/80 to-purple-900/60",
    glow: "rgba(139,92,246,0.25)",
  },
  {
    name: "Vitamins",
    icon: <Sun size={28} />,
    description: "Essential micronutrients",
    gradient: "from-orange-500 to-red-400",
    bg: "from-orange-950/80 to-red-900/60",
    glow: "rgba(249,115,22,0.25)",
  },
  {
    name: "Minerals",
    icon: <Award size={28} />,
    description: "Support bodily functions",
    gradient: "from-rose-500 to-pink-500",
    bg: "from-rose-950/80 to-pink-900/60",
    glow: "rgba(244,63,94,0.25)",
  },
];

/* ── Journey steps data ─────────────────────────────────────────────────── */
const steps = [
  {
    num: "01",
    icon: <Activity size={26} />,
    title: "Comprehensive Assessment",
    desc: "Our 50-point evaluation analyzes your metabolic type, DNA markers (optional), lifestyle, and health objectives.",
    badge: "Takes 8 minutes",
    BadgeIcon: Clock,
    gradient: "from-lime-600 to-emerald-600",
    glow: "rgba(101,163,13,0.3)",
  },
  {
    num: "02",
    icon: <Salad size={26} />,
    title: "Custom Nutrition Blueprint",
    desc: "Receive your tailored nutrition roadmap with chef-designed recipes, shopping lists, and macro targets.",
    badge: "Ready in 24 hours",
    BadgeIcon: CheckCircle,
    gradient: "from-emerald-600 to-teal-600",
    glow: "rgba(16,185,129,0.3)",
  },
  {
    num: "03",
    icon: <Users size={26} />,
    title: "Ongoing Optimization",
    desc: "Continuous support with plan adjustments, progress tracking, and dedicated 1:1 coaching sessions.",
    badge: "Dedicated nutritionist",
    BadgeIcon: Shield,
    gradient: "from-teal-600 to-cyan-600",
    glow: "rgba(20,184,166,0.3)",
  },
];

/* ── FAQ data ───────────────────────────────────────────────────────────── */
const faqItems = [
  {
    q: "How personalized are your diet plans?",
    a: "Each plan is customized based on your body type, activity level, food preferences, allergies, and specific health goals — using advanced nutritional algorithms and expert dietitian oversight.",
  },
  {
    q: "How soon can I expect to see results?",
    a: "Most members notice improvements in energy within the first week. Physical changes typically become apparent within 2–4 weeks of consistent adherence.",
  },
  {
    q: "Can I adjust my meal plan as I go?",
    a: "Absolutely. Plans evolve with you. We recommend reassessments every 4–6 weeks for optimal results as your body changes.",
  },
  {
    q: "Do I need special ingredients or equipment?",
    a: "No. Our meal plans use readily available ingredients. We focus on practical nutrition that fits into your real life without specialty products.",
  },
];

/* ══════════════════════════════════════════════════════════════════════════
   MAIN EXPORT
══════════════════════════════════════════════════════════════════════════ */
export default function DietPlanBannerExtended() {
  const floatingElements = useMemo(
    () =>
      [...Array(3)].map(() => ({
        x: Math.random() * 80,
        y: Math.random() * 80,
        scale: Math.random() * 0.5 + 0.5,
        rotate: Math.random() * 360,
        xTarget: Math.random() * 80,
        yTarget: Math.random() * 80,
        rotateTarget: Math.random() * 360,
        duration: Math.random() * 20 + 20,
        width: Math.random() * 200 + 80,
        height: Math.random() * 200 + 80,
        left: Math.random() * 100,
        top: Math.random() * 100,
      })),
    []
  );

  return (
    <div className="relative w-full overflow-hidden bg-white">

      {/* ══════════════════════════════════════════════════════════════
          SECTION 1 — "Backed by Nutritional Science"
          Dark card grid with colored gradients per nutrient
      ══════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-20 px-4 md:px-8 overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #0a0f0a 0%, #0d1a0d 50%, #071209 100%)",
        }}
      >
        {/* Ambient glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[250px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse, rgba(101,163,13,0.12) 0%, transparent 70%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Heading */}
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={STAGGER}
          >
            <motion.span
              variants={FADE_UP}
              className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/20 rounded-full px-4 py-1.5 text-lime-400 text-xs font-semibold uppercase tracking-widest mb-5"
            >
              <Zap size={12} />
              Science-Backed Nutrition
            </motion.span>
            <motion.h2
              variants={FADE_UP}
              className="text-4xl md:text-5xl font-extrabold text-white mb-4"
            >
              Backed by{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #84cc16, #22c55e)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Nutritional Science
              </span>
            </motion.h2>
            <motion.p variants={FADE_UP} className="text-gray-400 text-lg max-w-2xl mx-auto">
              Our approach balances all essential nutrients for optimal health, energy, and performance
            </motion.p>
          </motion.div>

          {/* Nutrient cards */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={STAGGER}
          >
            {nutrients.map((n, i) => (
              <motion.div
                key={i}
                variants={FADE_UP}
                whileHover={{ y: -8, scale: 1.03 }}
                className="group relative rounded-2xl p-5 flex flex-col items-center text-center cursor-default overflow-hidden border"
                style={{
                  background: `linear-gradient(145deg, ${n.bg.split(" ")[1].replace("from-", "")} 0%, transparent 100%)`,
                  background: "rgba(255,255,255,0.03)",
                  borderColor: "rgba(255,255,255,0.07)",
                  boxShadow: `0 0 0 0 ${n.glow}`,
                  transition: "box-shadow 0.3s ease",
                }}
                onHoverStart={(e) => {
                  e.currentTarget.style.boxShadow = `0 8px 30px -4px ${n.glow}, 0 0 0 1px rgba(255,255,255,0.1)`;
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
                }}
                onHoverEnd={(e) => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                }}
              >
                {/* Icon circle */}
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 text-white flex-shrink-0"
                  style={{
                    background: `linear-gradient(135deg, ${n.gradient.split(" ")[1].replace("from-", "var(--tw-gradient-from)")} 0%, transparent 100%)`,
                    background: `linear-gradient(135deg, ${
                      n.gradient.includes("blue") ? "#3b82f6, #06b6d4" :
                      n.gradient.includes("lime") ? "#84cc16, #22c55e" :
                      n.gradient.includes("amber") ? "#f59e0b, #eab308" :
                      n.gradient.includes("violet") ? "#8b5cf6, #a855f7" :
                      n.gradient.includes("orange") ? "#f97316, #ef4444" :
                      "#f43f5e, #ec4899"
                    })`,
                  }}
                >
                  {n.icon}
                </div>
                <h3 className="text-white font-bold text-sm mb-1">{n.name}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{n.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 2 — "Nourish Your Body, Transform Your Life"
          Hero banner with background gradient + CTAs uncommented
      ══════════════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-24 px-4 md:px-8 bg-white">
        {/* Floating blobs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {floatingElements.map((el, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-lime-200/20"
              initial={{ x: el.x, y: el.y, scale: el.scale, rotate: el.rotate }}
              animate={{
                x: [null, el.xTarget],
                y: [null, el.yTarget],
                rotate: [null, el.rotateTarget],
                transition: {
                  duration: el.duration,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "linear",
                },
              }}
              style={{
                width: `${el.width}px`,
                height: `${el.height}px`,
                left: `${el.left}%`,
                top: `${el.top}%`,
                filter: "blur(50px)",
              }}
            />
          ))}
        </div>

        <div className="relative z-10 max-w-5xl mx-auto">
          <motion.div
            className="text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={STAGGER}
          >
            {/* Badge */}
            <motion.div variants={FADE_UP} className="mb-6">
              <span className="inline-flex items-center gap-2 bg-lime-50 border border-lime-200 rounded-full px-5 py-2 text-lime-700 text-sm font-semibold">
                <Salad size={15} />
                PERSONALIZED NUTRITION PLANS
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h2
              variants={FADE_UP}
              className="text-5xl md:text-7xl font-extrabold mb-6 leading-[1.05] tracking-tight"
            >
              <span
                style={{
                  background: "linear-gradient(135deg, #166534 0%, #16a34a 40%, #84cc16 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Nourish Your Body,
              </span>
              <br />
              <span className="text-gray-900">Transform Your Life</span>
            </motion.h2>

            {/* Subtext */}
            <motion.p
              variants={FADE_UP}
              className="text-gray-500 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
            >
              Scientifically-designed meal plans tailored to your unique metabolism,
              lifestyle, and taste preferences — delivered fresh every day.
            </motion.p>

            {/* CTAs — now visible */}
            <motion.div
              variants={FADE_UP}
              className="flex flex-col sm:flex-row justify-center gap-4"
            >
              <motion.a
                href="/checkout"
                whileHover={{ scale: 1.04, boxShadow: "0 12px 30px -6px rgba(101,163,13,0.5)" }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-white text-base shadow-lg transition-shadow duration-300"
                style={{
                  background: "linear-gradient(135deg, #16a34a 0%, #84cc16 100%)",
                }}
              >
                Get Your Custom Plan
                <ArrowRight size={18} />
              </motion.a>
              <motion.a
                href="tel:+97145654390"
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lime-700 bg-lime-50 border border-lime-200 hover:border-lime-400 text-base transition-colors duration-200"
              >
                Speak to a Nutritionist
              </motion.a>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              variants={FADE_UP}
              className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-gray-400"
            >
              {["No credit card required", "2-minute assessment", "10,000+ happy customers"].map(
                (t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle size={15} className="text-lime-500" />
                    <span>{t}</span>
                  </div>
                )
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 3 — "Your Journey to Optimal Health"
          Dark background, gradient step cards with glow
      ══════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-24 px-4 md:px-8 overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #040d07 0%, #071a0e 60%, #040d07 100%)",
        }}
      >
        {/* Grid texture */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="journey-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#84cc16" strokeWidth="0.8" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#journey-grid)" />
          </svg>
        </div>

        {/* Top glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[300px] pointer-events-none"
          style={{
            background: "radial-gradient(ellipse, rgba(101,163,13,0.1) 0%, transparent 70%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Heading */}
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={STAGGER}
          >
            <motion.span
              variants={FADE_UP}
              className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/20 rounded-full px-4 py-1.5 text-lime-400 text-xs font-semibold uppercase tracking-widest mb-5"
            >
              <Activity size={12} />
              How It Works
            </motion.span>
            <motion.h2
              variants={FADE_UP}
              className="text-4xl md:text-5xl font-extrabold text-white mb-4"
            >
              Your Journey to{" "}
              <span
                style={{
                  background: "linear-gradient(90deg, #84cc16, #22c55e)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Optimal Health
              </span>
            </motion.h2>
            <motion.p variants={FADE_UP} className="text-gray-400 text-lg max-w-2xl mx-auto">
              Our comprehensive nutrition system adapts to your body's changing needs through every phase
            </motion.p>
          </motion.div>

          {/* Steps */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
            {/* Connecting line (desktop) */}
            <div className="hidden lg:block absolute top-16 left-[calc(16.6%+24px)] right-[calc(16.6%+24px)] h-px z-0"
              style={{
                background: "linear-gradient(90deg, rgba(101,163,13,0.3), rgba(101,163,13,0.6), rgba(101,163,13,0.3))",
              }}
            />

            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                variants={{ hidden: { y: 40, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.6, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] } } }}
                whileHover={{ y: -6 }}
                className="relative rounded-3xl p-8 border transition-all duration-300 overflow-hidden"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  borderColor: "rgba(255,255,255,0.08)",
                  backdropFilter: "blur(10px)",
                }}
                onHoverStart={(e) => {
                  e.currentTarget.style.borderColor = "rgba(101,163,13,0.3)";
                  e.currentTarget.style.boxShadow = `0 20px 60px -10px ${step.glow}`;
                }}
                onHoverEnd={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                {/* Step number (large background) */}
                <div
                  className="absolute -top-4 -right-2 text-8xl font-black leading-none pointer-events-none select-none"
                  style={{ color: "rgba(255,255,255,0.03)" }}
                >
                  {step.num}
                </div>

                {/* Icon + number */}
                <div className="flex items-center gap-4 mb-6 relative z-10">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-white flex-shrink-0 shadow-lg"
                    style={{
                      background: `linear-gradient(135deg, ${
                        step.gradient.includes("lime") ? "#84cc16, #22c55e" :
                        step.gradient.includes("emerald") ? "#10b981, #14b8a6" :
                        "#14b8a6, #0891b2"
                      })`,
                      boxShadow: `0 8px 24px -4px ${step.glow}`,
                    }}
                  >
                    {step.icon}
                  </div>
                  <span
                    className="text-4xl font-black leading-none"
                    style={{
                      background: `linear-gradient(135deg, ${
                        step.gradient.includes("lime") ? "#84cc16, #22c55e" :
                        step.gradient.includes("emerald") ? "#10b981, #14b8a6" :
                        "#14b8a6, #0891b2"
                      })`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    {step.num}
                  </span>
                </div>

                <h3 className="text-white font-bold text-xl mb-3 relative z-10">{step.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6 relative z-10">{step.desc}</p>

                {/* Badge */}
                <div
                  className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs font-semibold relative z-10"
                  style={{
                    background: "rgba(101,163,13,0.12)",
                    color: "#86efac",
                    border: "1px solid rgba(101,163,13,0.2)",
                  }}
                >
                  <step.BadgeIcon size={13} />
                  {step.badge}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          SECTION 4 — FAQ
          Accordion-style, elegant dark cards
      ══════════════════════════════════════════════════════════════ */}
      <section className="py-20 px-4 md:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-14"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={STAGGER}
          >
            <motion.span
              variants={FADE_UP}
              className="inline-flex items-center gap-2 bg-lime-50 border border-lime-200 rounded-full px-4 py-1.5 text-lime-700 text-xs font-semibold uppercase tracking-widest mb-5"
            >
              <HelpCircle size={12} />
              Common Questions
            </motion.span>
            <motion.h2 variants={FADE_UP} className="text-4xl font-extrabold text-gray-900 mb-4">
              Everything You Need to Know
            </motion.h2>
            <motion.p variants={FADE_UP} className="text-gray-500 text-lg">
              Answers to the most common questions about our nutrition plans
            </motion.p>
          </motion.div>

          <motion.div
            className="space-y-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={STAGGER}
          >
            {faqItems.map((item, i) => (
              <motion.div
                key={i}
                variants={FADE_UP}
                whileHover={{ y: -2 }}
                className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-lime-200 shadow-sm hover:shadow-md transition-all duration-300 group"
              >
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 rounded-xl bg-lime-50 border border-lime-100 flex items-center justify-center flex-shrink-0 group-hover:bg-lime-100 transition-colors">
                    <Salad size={18} className="text-lime-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg mb-2">{item.q}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════
          FINAL CTA BANNER
          Full-bleed gradient with floating shapes
      ══════════════════════════════════════════════════════════════ */}
      <section
        className="relative py-20 px-4 md:px-8 overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #14532d 0%, #166534 40%, #15803d 70%, #84cc16 100%)",
        }}
      >
        {/* Decorative circles */}
        <div className="absolute -right-24 -top-24 w-64 h-64 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -left-16 -bottom-16 w-80 h-80 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute top-8 left-1/2 -translate-x-1/2 w-1/2 h-px bg-white/10 pointer-events-none" />

        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={STAGGER}
          >
            <motion.h2
              variants={FADE_UP}
              className="text-4xl md:text-5xl font-extrabold text-white mb-5"
            >
              Start Your Transformation Today
            </motion.h2>
            <motion.p
              variants={FADE_UP}
              className="text-white/80 text-xl max-w-2xl mx-auto mb-10"
            >
              Take our 2-minute assessment and receive your personalized nutrition plan within 24 hours
            </motion.p>
            <motion.div variants={FADE_UP}>
              <motion.a
                href="/checkout"
                whileHover={{ scale: 1.05, boxShadow: "0 16px 40px -8px rgba(0,0,0,0.4)" }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 px-10 py-5 bg-white text-lime-700 font-extrabold rounded-full text-lg shadow-xl transition-all duration-300"
              >
                Get Your Custom Plan
                <ArrowRight size={20} />
              </motion.a>
            </motion.div>
            <motion.div
              variants={FADE_UP}
              className="mt-8 flex flex-wrap justify-center gap-6 text-white/70 text-sm"
            >
              {["No credit card required", "2-minute assessment", "Nutritionist approved"].map(
                (t) => (
                  <div key={t} className="flex items-center gap-2">
                    <CheckCircle size={15} />
                    <span>{t}</span>
                  </div>
                )
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

    </div>
  );
}